import { formatCurrency } from '@utils/currency';
import { Box } from './Box';
import { Bolt, LoadingSpinner } from './Icons';
import { Modal } from './Modal';
import { Text } from './Text';
import { AgentAvatar } from './ui/AgentAvatar';
import { IoCheckmark } from 'react-icons/io5';
import { Button } from './Button';
import { useNavigationStore } from '@base/stores/navigation';
import type React from 'react';
import type { Plan } from '@base/api';
import { Alert } from './ui/Alert';
import Link, { Target } from './Link';

type AutofunSubscriptionModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  agentPlan?: Plan;
  closeModal: () => void;
  onSubmit: () => void;
};

export const AutofunSubscriptionModal: React.FC<
  AutofunSubscriptionModalProps
> = ({ isOpen, closeModal, isLoading, agentPlan, onSubmit }) => {
  const { navigationStore } = useNavigationStore();
  const { avatar } = navigationStore;

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      modalContainerClassName="p-0 bg-elz-neutral-2 max-w-[384px]"
    >
      <Box className="flex flex-col items-center justify-center gap-8 px-16 py-24 border-b-1 border-b-elz-neutral-6">
        {isLoading ? <LoadingSpinner /> : <Bolt />}
        <Text variant="subtitle" className="text-[1.8rem] leading-tight">
          {isLoading ? 'Pending payment' : `It's time to upgrade your plan`}
        </Text>
        <Text variant="secondary">
          {isLoading
            ? 'Continue making your payment to deploy your AI agent.'
            : 'To continue, subscribe with a 7-day free trial.'}
        </Text>
        {!isLoading && !agentPlan && (
          <Alert variant="danger">
            <p>
              We were unable to load the plan details. Please try again and if
              the issue persists,{' '}
              <Link href="/support" target={Target.Blank} className="underline">
                contact support.
              </Link>
            </p>
          </Alert>
        )}
        {!isLoading && agentPlan && (
          <>
            <Box className="py-16 w-full">
              <Box className="bg-elz-neutral-3 p-12 rounded-8 flex-row items-center w-full">
                <AgentAvatar src={avatar} className="size-64 rounded-8" />
                <Box className="flex-1 pl-16">
                  <Text variant="secondary">AI agent</Text>
                  <Text
                    variant="subtitle"
                    className="text-[1.8rem] leading-tight"
                  >
                    {formatCurrency(agentPlan.price)}/mo
                    <Text
                      variant="secondary"
                      className="text-[1.2rem] inline-flex pl-6"
                      weight={400}
                      as="span"
                    >
                      + LLM usage costs
                    </Text>
                  </Text>
                </Box>
              </Box>
            </Box>
            <Box className="w-full gap-8">
              <Text variant="primary" weight={500}>
                What you get
              </Text>
              <Box className="flex-row items-center gap-6">
                <IoCheckmark className="size-18" /> Chat with your agent all the
                time
              </Box>
              <Box className="flex-row items-center gap-6">
                <IoCheckmark className="size-18" /> Keep tabs on your agent's
                activity
              </Box>
              <Box className="flex-row items-center gap-6">
                <IoCheckmark className="size-18" /> View important analytics for
                your agent
              </Box>
              <Box className="flex-row items-center gap-6">
                <IoCheckmark className="size-18" /> Customize your agent further
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Box className="p-16">
        <Box className="flex-row items-center justify-center gap-[16px] text-[14px] font-medium leading-[20px]">
          {!isLoading && (
            <Box className="flex-row gap-16 *:flex-1 w-full">
              <Button variant="ghost" onClick={closeModal}>
                No thanks
              </Button>
              <Button disabled={!agentPlan} onClick={onSubmit}>
                Start free trial
              </Button>
            </Box>
          )}
          {isLoading && (
            <Button variant="ghost" onClick={closeModal} className="flex-1">
              Cancel
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
